import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeftCircle,
  Calendar,
  CheckCircle2,
  FileText,
  Trash2,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAssignment, useLearnerAssignmentSubmission, useSubmitAssignment } from '@/hooks/useAssignments'
import { useCourse } from '@/hooks/useCourses'
import { useNotifications } from '@/contexts/NotificationContext'
import { assignmentService } from '@/services/assignmentService'
import { paths } from '@/routes/path'

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  const decimals = value >= 10 || exponent === 0 ? 0 : 1
  return `${value.toFixed(decimals)} ${units[exponent]}`
}

export function SubmitAssignmentPage() {
  const navigate = useNavigate()
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const { addNotification } = useNotifications()

  const { data: assignment, isLoading: assignmentLoading, error: assignmentError } = useAssignment(
    assignmentId ?? ''
  )
  const {
    data: mySubmission,
    isLoading: submissionLoading,
  } = useLearnerAssignmentSubmission(assignmentId ?? '')
  const { data: course } = useCourse(assignment?.courseId)

  const submitAssignment = useSubmitAssignment()

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (mySubmission && mySubmission.status === 'submitted') {
      setSelectedFiles([])
    }
  }, [mySubmission])

  const isSubmitting = submitAssignment.isPending
  const isLoading = assignmentLoading || submissionLoading

  const hasSubmitted = mySubmission && ['submitted', 'graded', 'returned'].includes(mySubmission.status)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files])
      setValidationError(null)
      event.target.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))
  }

  const confirmSubmission = () => {
    if (!assignmentId) return
    if (selectedFiles.length === 0) {
      setValidationError('Please attach at least one file before submitting.')
      return
    }
    setConfirmationOpen(true)
  }

  const handleSubmit = async () => {
    if (!assignmentId) return
    setConfirmationOpen(false)

    const payload = {
      assignmentId,
      files: selectedFiles,
      submit: true,
    }

    try {
      const result = await submitAssignment.mutateAsync(payload)
      if (result) {
        addNotification({
          title: 'Assignment submitted',
          message: 'Your work has been uploaded successfully.',
          type: 'general',
          priority: 'medium',
          isRead: false,
          isArchived: false,
        })
        navigate(paths.app.assignments.detail.build(assignmentId))
      }
    } catch (error) {
      setValidationError('We could not submit your assignment. Please try again.')
    }
  }

  const downloadAttachment = async (fileUrl: string, fileName: string) => {
    try {
      await assignmentService.downloadFile(fileUrl, fileName)
    } catch (error) {
      addNotification({
        title: 'Download failed',
        message: 'We could not download this file. Please try again.',
        type: 'general',
        priority: 'high',
        isRead: false,
        isArchived: false,
      })
    }
  }

  const submissionStatus = useMemo(() => {
    if (!mySubmission) return null
    const submittedAt = mySubmission.submittedAt
      ? new Date(mySubmission.submittedAt).toLocaleString()
      : 'Not submitted'

    return {
      ...mySubmission,
      submittedAt,
    }
  }, [mySubmission])

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (assignmentError || !assignmentId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Unable to load assignment
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              The assignment you are trying to submit might have been removed or is currently unavailable.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (!assignment) {
    return null
  }

  const dueDate = new Date(assignment.dueDate)
  const isOverdue = dueDate < new Date()
  const courseTitle = course?.title ?? assignment.courseId

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3 text-sm font-medium text-red-600">
            <Upload className="h-4 w-4" />
            Submit assignment
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">{assignment.title}</h1>
          <p className="mt-2 text-gray-600">
            Upload your files and double-check the instructions before submitting. You can re-upload before the due date if retries are allowed.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(paths.app.assignments.detail.build(assignmentId))}
          className="flex items-center gap-2"
        >
          <ArrowLeftCircle className="h-4 w-4" />
          Back to assignment
        </Button>
      </header>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Your submission</CardTitle>
            <CardDescription>
              Attach your completed work. Accepted formats include PDF, Word documents, slides, spreadsheets, and images.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {hasSubmitted && submissionStatus && (
              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Last submitted: {submissionStatus.submittedAt}
                </div>
                <p className="mt-2 text-xs text-green-700">
                  You can upload again before the deadline if the instructor allows retries. Only the latest submission will be graded.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Upload files</label>
              <Input type="file" multiple onChange={handleFileChange} className="cursor-pointer" />
              <p className="text-xs text-gray-500">Maximum file size is determined by your browser settings. Upload one or more files at once.</p>
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Files to be submitted</p>
                <ul className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium text-gray-800">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove file</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {validationError && (
              <p className="text-sm text-red-600">{validationError}</p>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                onClick={confirmSubmission}
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                {isSubmitting ? 'Submitting...' : 'Submit assignment'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Assignment details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">Due date</p>
                  <p className={`text-gray-600 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                    {dueDate.toLocaleString()}
                  </p>
                </div>
              </div>
              <div>
                <p className="font-medium text-gray-800">Course</p>
                <p className="text-gray-600">{courseTitle}</p>
              </div>
              <div>
                <p className="font-medium text-gray-800">Maximum points</p>
                <p className="text-gray-600">{assignment.maxPoints}</p>
              </div>
              {assignment.settings?.allowRetries && (
                <Badge className="bg-blue-100 text-blue-700">Retries allowed</Badge>
              )}
              {assignment.settings?.timeSensitive && (
                <Badge className="bg-orange-100 text-orange-700">Time sensitive</Badge>
              )}
            </CardContent>
          </Card>

          {assignment.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Reference files</CardTitle>
                <CardDescription>Download the instructions or templates provided by your instructor.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {assignment.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{attachment.title ?? attachment.fileName ?? 'Attachment'}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.mimeType} • {formatBytes(attachment.fileSize)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadAttachment(attachment.url, attachment.title ?? attachment.fileName ?? 'attachment')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={confirmationOpen}
        onConfirm={handleSubmit}
        onCancel={() => setConfirmationOpen(false)}
        title="Submit assignment?"
        description="Once you submit, the instructor will be able to review your files. You can still upload new files before the deadline if retries are allowed."
        confirmLabel={isSubmitting ? 'Submitting...' : 'Submit now'}
        confirmTone="default"
      />
    </div>
  )
}

export default SubmitAssignmentPage
